import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../context/I18nContext';
import { Icons } from '../icons/Icons';
import { formatSezione, getMultipolarLabel } from '../../utils/cableLogic';
import { translateColorForDisplay } from '../../utils/colorUtils';
import CableTypeIcon from '../icons/CableTypeIcon';
import CableCatalogItemCard from '../ui/CableCatalogItemCard';
import {
  getCableCatalogStatus,
  getCatalogSectionDisplay,
  getCatalogSectionStandard,
  isCatalogItemCompatibleWithProject,
  matchesCatalogSearch,
  normalizeInternalCableCode
} from '../../utils/cableCatalog';

const UNRESOLVED_VALUE = '__unresolved__';

const CableCatalogSelector = ({ cable, items = [], onAssign, onUnlink, onRealign, useAWG = false, isMultipolar = false, headerContent = null, trailingControl = null }) => {
  const { t, language } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const assignment = cable?.catalogAssignment;
  const status = getCableCatalogStatus(cable);

  const assignableItems = useMemo(() => items.filter((item) => normalizeInternalCableCode(item.codiceInterno)), [items]);
  const compatibleItems = useMemo(
    () => assignableItems.filter((item) => isCatalogItemCompatibleWithProject(item, useAWG)),
    [assignableItems, useAWG]
  );
  const resolvedItem = useMemo(() => {
    if (!assignment) return null;
    return assignableItems.find((item) => String(item.id) === String(assignment.itemId)) ||
      assignableItems.find((item) => normalizeInternalCableCode(item.codiceInterno).toLocaleLowerCase() === normalizeInternalCableCode(assignment.codiceInterno).toLocaleLowerCase()) ||
      null;
  }, [assignableItems, assignment]);
  const hasDifferentSectionStandard = Boolean(resolvedItem && !isCatalogItemCompatibleWithProject(resolvedItem, useAWG));
  const assignmentNeedsAttention = Boolean(assignment && (status !== 'resolved' || hasDifferentSectionStandard));

  const filteredItems = useMemo(
    () => compatibleItems.filter((item) => matchesCatalogSearch(item, query)),
    [compatibleItems, query]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const openModal = () => {
    setQuery('');
    setSelectedValue(assignment ? (resolvedItem ? String(resolvedItem.id) : UNRESOLVED_VALUE) : '');
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const applySelection = () => {
    if (selectedValue === UNRESOLVED_VALUE) {
      closeModal();
      return;
    }
    if (!selectedValue) {
      if (assignment) onUnlink();
      closeModal();
      return;
    }
    const item = assignableItems.find((candidate) => String(candidate.id) === selectedValue);
    if (item) onAssign(item);
    closeModal();
  };

  const getItemTechnicalSummary = (item) => {
    if (!isMultipolar) {
      return `${getCatalogSectionDisplay(item)} · ${translateColorForDisplay(item.colore, language) || '—'}`;
    }

    const itemUsesAWG = getCatalogSectionStandard(item) === 'awg';
    const poles = Array.from({ length: Number.parseInt(item.numeroPoli, 10) || 0 }, (_, index) => ({
      colore: item.poliColori?.[index] || '',
      sezione: Number.parseFloat(item.sezione) || 0.5
    }));
    const label = getMultipolarLabel(poles, itemUsesAWG);
    const localizedPoleColors = (item.poliColori || [])
      .filter(Boolean)
      .map((color) => translateColorForDisplay(color, language));
    const jacketColor = item.coloreGuaina
      ? `${t('userSettingsPage.cableCatalog.outerSheathColor')}: ${translateColorForDisplay(item.coloreGuaina, language)}`
      : '';
    return [
      label ? `${label}${itemUsesAWG ? ' AWG' : ''}` : '—',
      localizedPoleColors.join(', ') || '—',
      jacketColor
    ].filter(Boolean).join(' · ');
  };

  const getAssignedTechnicalSummary = () => {
    if (resolvedItem) return getItemTechnicalSummary(resolvedItem);
    if (isMultipolar) {
      const poles = Array.isArray(cable?.poli) ? cable.poli : [];
      const sections = [...new Set(poles.map((pole) => pole.sezione).filter(Boolean))];
      const localizedPoleColors = poles
        .map((pole) => pole.colore)
        .filter(Boolean)
        .map((color) => translateColorForDisplay(color, language));
      return `${poles.length} ${t('cableForm.catalogPolesShort')} · ${sections.map((section) => formatSezione(section, useAWG)).join(', ') || '—'} · ${localizedPoleColors.join(', ') || '—'}`;
    }
    return `${translateColorForDisplay(cable?.colore, language) || '—'} · ${formatSezione(cable?.sezione || 0.5, useAWG)}`;
  };

  const renderCableTypeIcon = (className) => (
    <CableTypeIcon
      isMultipolar={isMultipolar}
      color={cable?.colore}
      jacketColor={resolvedItem?.coloreGuaina || ''}
      poleColors={Array.isArray(cable?.poli) ? cable.poli.map((pole) => pole.colore) : []}
      poleCount={Array.isArray(cable?.poli) ? cable.poli.length : 0}
      className={className}
    />
  );

  const catalogButton = (
    <button
      type="button"
      onClick={openModal}
      className={`relative h-10 w-[72px] flex-shrink-0 rounded-lg border flex items-center justify-center overflow-visible transition-all focus:ring-2 focus:ring-blue-500 ${assignment ? (assignmentNeedsAttention ? 'border-amber-300 bg-white text-amber-700 hover:bg-amber-50' : 'border-blue-300 bg-white text-blue-700 hover:bg-blue-100') : 'border-gray-300 bg-white text-gray-500 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50'}`}
      title={assignment ? t('cableForm.editCatalogAssignmentTitle', { code: assignment.codiceInterno || '—' }) : t('cableForm.assignCatalogTitle')}
      aria-label={assignment ? t('cableForm.editCatalogAssignmentTitle', { code: assignment.codiceInterno || '—' }) : t('cableForm.assignCatalogTitle')}
    >
      {renderCableTypeIcon('h-9 w-16 object-contain')}
      <span className={`absolute -right-1 -bottom-1 h-[18px] w-[18px] rounded-full border-2 border-white text-[11px] leading-none font-bold flex items-center justify-center ${assignment ? (assignmentNeedsAttention ? 'bg-amber-500 text-white' : 'bg-green-500 text-white') : 'bg-blue-600 text-white'}`}>
        <span className="relative -top-px">{assignment ? (assignmentNeedsAttention ? '!' : '✓') : '+'}</span>
      </span>
    </button>
  );

  return (
    <>
      <div className="w-full min-w-0">
        <div className="flex items-center gap-3">
          {headerContent}
          <div className="ml-auto flex items-center justify-end gap-3">
            {!assignment && catalogButton}
            {trailingControl}
          </div>
        </div>

        {assignment && (
          <div className={`mt-3 flex w-full items-center gap-3 rounded-lg border px-3 py-2 animate-fade-in ${assignmentNeedsAttention ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50/70'}`}>
            {catalogButton}
            <div className="min-w-0 text-left">
              <div className={`text-xs font-semibold ${assignmentNeedsAttention ? 'text-amber-900' : 'text-blue-900'}`}>
                {assignment.codiceInterno || '—'}
                {resolvedItem?.descrizione ? ` · ${resolvedItem.descrizione}` : ''}
              </div>
              <div className="mt-0.5 text-[11px] text-gray-600">
                {[resolvedItem?.produttore, getAssignedTechnicalSummary()].filter(Boolean).join(' · ')}
              </div>
              {hasDifferentSectionStandard && (
                <div className="mt-0.5 text-[11px] font-medium text-amber-700">
                  {t('cableForm.differentSectionStandard')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-[100000] flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cable-catalog-modal-title">
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-16 h-10 rounded-lg border border-blue-200 bg-blue-50 flex items-center justify-center flex-shrink-0">
                  {renderCableTypeIcon('h-9 w-14 object-contain')}
                </div>
                <div>
                  <h3 id="cable-catalog-modal-title" className="text-lg font-semibold text-gray-900">{t('cableForm.assignCatalogModalTitle')}</h3>
                  <p className="text-sm text-gray-500 mt-1">{t('cableForm.assignCatalogModalSubtitle')}</p>
                </div>
              </div>
              <button type="button" onClick={closeModal} className="p-1 text-gray-400 hover:text-gray-700 rounded" aria-label={t('common.close')}>
                <Icons.X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                <p className="font-medium">{t('cableForm.catalogOptionalTitle')}</p>
                <p className="mt-1 text-blue-800">{t(isMultipolar ? 'cableForm.catalogOptionalMultipolarHelp' : 'cableForm.catalogOptionalStandardHelp')}</p>
              </div>

              {assignment && status !== 'resolved' && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <span>{t(`cableForm.catalogStatusHelp.${status}`, { code: assignment.codiceInterno || '—' })}</span>
                  {status === 'structure-mismatch' && onRealign && (
                    <button type="button" onClick={() => { onRealign(); closeModal(); }} className="px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 whitespace-nowrap">
                      {t('cableForm.realignCatalog')}
                    </button>
                  )}
                </div>
              )}

              <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('cableForm.searchCatalog')} className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" autoFocus />

              <div className="space-y-2">
                <button type="button" onClick={() => setSelectedValue('')} className={`w-full p-4 rounded-lg border text-left transition-colors flex items-center gap-3 ${selectedValue === '' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}>
                  <span className={`w-4 h-4 rounded-full border flex-shrink-0 ${selectedValue === '' ? 'border-[5px] border-blue-600' : 'border-gray-400'}`} />
                  <div className="h-11 w-16 flex-shrink-0 rounded-md border border-gray-200 bg-white p-1 flex items-center justify-center">
                    {renderCableTypeIcon('h-full w-full object-contain')}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{t('cableForm.genericCable')}</div>
                    <div className="text-xs text-gray-500 mt-1">{t('cableForm.genericCableHelp')}</div>
                  </div>
                </button>

                {assignment && !resolvedItem && (
                  <button type="button" onClick={() => setSelectedValue(UNRESOLVED_VALUE)} className={`w-full p-4 rounded-lg border text-left flex items-center gap-3 ${selectedValue === UNRESOLVED_VALUE ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                    <span className={`w-4 h-4 rounded-full border flex-shrink-0 ${selectedValue === UNRESOLVED_VALUE ? 'border-[5px] border-amber-600' : 'border-gray-400'}`} />
                    <div className="h-11 w-16 flex-shrink-0 rounded-md border border-amber-200 bg-white p-1 flex items-center justify-center">
                      {renderCableTypeIcon('h-full w-full object-contain')}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">{assignment.codiceInterno}</div>
                      <div className="text-xs text-amber-700 mt-1">{t('cableForm.catalogStatus.unresolved')}</div>
                    </div>
                  </button>
                )}

                {hasDifferentSectionStandard && resolvedItem && (
                  <CableCatalogItemCard
                    item={resolvedItem}
                    isMultipolar={isMultipolar}
                    selectable
                    selected={selectedValue === String(resolvedItem.id)}
                    warning
                    onClick={() => setSelectedValue(String(resolvedItem.id))}
                    badge={(
                      <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                          {t('cableForm.differentSectionStandard')}
                      </span>
                    )}
                  />
                )}

                {filteredItems.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                    {compatibleItems.length === 0 ? t('cableForm.noCompatibleCatalogCables') : t('cableForm.noCatalogResults')}
                  </div>
                ) : filteredItems.map((item) => {
                  const itemValue = String(item.id);
                  const selected = selectedValue === itemValue;
                  return (
                    <CableCatalogItemCard
                      key={item.id}
                      item={item}
                      isMultipolar={isMultipolar}
                      selectable
                      selected={selected}
                      onClick={() => setSelectedValue(itemValue)}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-white">{t('common.cancel')}</button>
              <button type="button" onClick={applySelection} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">{t('cableForm.applyCatalogAssignment')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CableCatalogSelector;
